import React, { useEffect } from "react";

import { Navigate, useParams } from "react-router-dom";
import CheckTypesList from "./CheckTypesList";
import {
  useGetCompanyWithDetails,
  useGetCompanyEx,
} from "../../../api/CompaniesApi";
import ErrorMessageContainer from "../../../shared-components/ErrorMessageContainer";
import LoadingSpinner from "../../../shared-components/LoadingSpinner";
import { usePermissionValidator } from "../../../api/UserPermissionValidators";

import { InformationCircleIcon } from "@heroicons/react/24/outline";
import ReactTooltip from "react-tooltip";

const CheckTypes = () => {
  const { companyId, subid } = useParams();
  const permissions = usePermissionValidator();
  const {
    isLoadingExtended,
    mutate: loadDetail,
    blueprints,
    error: errorLoadingDetails,
  } = useGetCompanyWithDetails();

  const {
    isLoading: isLoadingSubCompany,
    mutate: loadSubCompany,
    blueprints: subCompanyBlueprints,
    error: errorLoadingSubCompany,
  } = useGetCompanyWithDetails();

  const {
    isLoading: isLoadingCompanyEx,
    mutate: loadCompanyEx,
    data: companyEx,
  } = useGetCompanyEx();

  useEffect(() => {
    if (companyId) {
      loadDetail(companyId);
      loadCompanyEx(companyId);
    }
  }, [companyId, loadDetail, loadCompanyEx]);

  useEffect(() => {
    if (subid) {
      loadSubCompany(subid);
      loadCompanyEx(subid);
    }
  }, [subid, loadSubCompany, loadCompanyEx]);

  if (
    (!subid && !permissions.companies.canViewBlueprints) ||
    (subid && !permissions.subcompanies.canViewBlueprints)
  ) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (isLoadingExtended || isLoadingSubCompany || isLoadingCompanyEx)
    return <LoadingSpinner />;

  const activeId = subid || companyId;
  const activeCompanyEx = subid ? companyEx : companyEx;
  const isSubCompany = !!subid;

  const canUpdateDefaultBlueprint = isSubCompany
    ? permissions.subcompanies.canEditSubcompanySetting
    : permissions.companies.canEditCompanySetting;

  const reloadCompanyInfo = () => {
    if (activeId) {
      if (isSubCompany) {
        loadSubCompany(activeId);
      } else {
        loadDetail(activeId);
      }
      loadCompanyEx(activeId);
    }
  };

  return (
    <div className="flex flex-col gap-4 bg-gray-50 p-7  rounded-md w-[800px]">
      <h2 className="text-md font-medium mb-4 flex gap-2 items-center">
        Check Type Assignment
        <InformationCircleIcon
          className="w-5 text-gray-500"
          data-tip={
            "Check Types are subscription products that manage Behavior Flags, social media sources, and the lookback period."
          }
        />
        <ReactTooltip
          className="hover:visible:opacity-1 w-96"
          place="right"
          delayHide={1000}
          effect="solid"
        />
      </h2>
      {(errorLoadingDetails || errorLoadingSubCompany) && (
        <ErrorMessageContainer message="Error loading Check Type Assignment" />
      )}
      {isSubCompany && (
        <CheckTypesList
          companyUUID={subid}
          company={activeCompanyEx}
          blueprintsList={subCompanyBlueprints}
          isReadOnly={
            !permissions.subcompanies.canEditBlueprints ||
            blueprints.length === 0
          }
          parentBlueprintsUUIDs={blueprints.map(
            (blueprint) => blueprint.blueprint_uuid
          )}
          canUpdateDefaultBlueprint={canUpdateDefaultBlueprint}
          callbackOnChange={reloadCompanyInfo}
        />
      )}
      {!isSubCompany && companyId && (
        <CheckTypesList
          companyUUID={companyId}
          company={activeCompanyEx}
          blueprintsList={blueprints}
          isReadOnly={!permissions.companies.canEditBlueprints}
          parentBlueprintsUUIDs={[]}
          canUpdateDefaultBlueprint={canUpdateDefaultBlueprint}
          callbackOnChange={reloadCompanyInfo}
        />
      )}
    </div>
  );
};

export default CheckTypes;
